import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../../auth/context/AuthContext';
import { getMyProfile, updateMyProfile, changeMyPassword } from '../services/userService';
import { AVATAR_CATALOG, getAvatarEmoji } from '../utils/avatarCatalog';

const section = {
  hidden: { opacity: 0, y: 32 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } },
};

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1 } },
};

export default function ProfilePage() {
  const { user, updateUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [name, setName] = useState('');
  const [selectedAvatar, setSelectedAvatar] = useState(0);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [pwSaving, setPwSaving] = useState(false);
  const [pwMsg, setPwMsg] = useState({ type: '', text: '' });

  useEffect(() => {
    getMyProfile().then((data) => {
      setProfile(data);
      setName(data.name || '');
      setSelectedAvatar(data.avatarIndex || 0);
    });
  }, []);

  async function handleSaveProfile(event) {
    event.preventDefault();
    setSaving(true);
    setSaveMsg('');

    try {
      const updated = await updateMyProfile({ name, avatarIndex: selectedAvatar });
      setProfile(updated);
      updateUser({ name: updated.name, avatarIndex: updated.avatarIndex });
      setSaveMsg('Perfil guardado ✓');
      setTimeout(() => setSaveMsg(''), 3000);
    } catch (_error) {
      setSaveMsg('Error al guardar');
    } finally {
      setSaving(false);
    }
  }

  async function handleChangePassword(event) {
    event.preventDefault();
    setPwSaving(true);
    setPwMsg({ type: '', text: '' });

    try {
      await changeMyPassword({ currentPassword, newPassword });
      setPwMsg({ type: 'success', text: 'Contraseña actualizada ✓' });
      setCurrentPassword('');
      setNewPassword('');
    } catch (error) {
      const message = error?.response?.data || 'Error al cambiar la contraseña';
      setPwMsg({ type: 'error', text: message });
    } finally {
      setPwSaving(false);
    }
  }

  if (!profile) {
    return (
      <div className="rounded-[30px] border border-[var(--app-border)] bg-[var(--app-surface)] p-12 flex items-center justify-center gap-3 text-slate-500">
        <span className="w-5 h-5 border-2 border-slate-300 border-t-[var(--app-accent)] rounded-full animate-spin" />
        Cargando perfil...
      </div>
    );
  }

  return (
    <motion.div variants={stagger} initial="hidden" animate="visible" className="space-y-6">
      {/* Header */}
      <motion.div variants={section} className="rounded-[30px] border border-[var(--app-border)] bg-[var(--app-surface)] p-8 shadow-[0_24px_60px_rgba(15,23,42,0.06)] backdrop-blur-xl">
        <div className="flex items-center gap-6">
          <div className="w-24 h-24 rounded-[28px] bg-gradient-to-br from-orange-400 to-amber-500 flex items-center justify-center text-5xl shadow-lg shadow-orange-200/50">
            {getAvatarEmoji(profile.avatarIndex)}
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-[0.32em] text-slate-400">Mi perfil</p>
            <h1 className="mt-2 text-[48px] leading-[0.96] font-semibold tracking-[-0.06em] text-slate-950">{profile.name}</h1>
            <p className="mt-2 text-lg text-slate-500">{profile.email} · <span className="font-medium text-slate-700">{profile.role}</span></p>
          </div>
        </div>
      </motion.div>

      {/* Avatar selector + Name */}
      <motion.form variants={section} onSubmit={handleSaveProfile} className="rounded-[30px] border border-[var(--app-border)] bg-[var(--app-surface)] p-8 space-y-6 shadow-[0_24px_60px_rgba(15,23,42,0.06)] backdrop-blur-xl">
        <div>
          <p className="text-[11px] uppercase tracking-[0.28em] text-slate-400">Personalización</p>
          <h2 className="mt-2 text-2xl font-medium text-slate-950">Elige tu avatar</h2>
        </div>

        <div className="grid grid-cols-4 sm:grid-cols-6 gap-3">
          {AVATAR_CATALOG.map((avatar, index) => (
            <button
              key={index}
              type="button"
              onClick={() => setSelectedAvatar(index)}
              className={`w-full aspect-square rounded-[22px] text-4xl flex items-center justify-center transition-all ${
                selectedAvatar === index
                  ? 'bg-gradient-to-br from-orange-400 to-amber-500 shadow-lg shadow-orange-200/50 scale-110 ring-2 ring-orange-300'
                  : 'bg-[rgba(245,245,247,0.9)] hover:bg-white hover:shadow-md border border-[var(--app-border)]'
              }`}
            >
              {avatar.emoji}
            </button>
          ))}
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-2">Nombre</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="app-input max-w-md"
            />
          </div>

          <div className="flex items-center gap-4">
            <button
              type="submit"
              disabled={saving}
              className="rounded-[22px] bg-[#0a84ff] hover:bg-[#0077ed] text-white px-6 py-4 font-medium shadow-[0_14px_24px_rgba(10,132,255,0.22)] transition disabled:opacity-50"
            >
              {saving ? 'Guardando...' : 'Guardar cambios'}
            </button>
            {saveMsg && <span className="text-sm font-medium text-emerald-600">{saveMsg}</span>}
          </div>
        </div>
      </motion.form>

      {/* Change password */}
      <motion.form variants={section} onSubmit={handleChangePassword} className="rounded-[30px] border border-[var(--app-border)] bg-[var(--app-surface)] p-8 space-y-5 shadow-[0_24px_60px_rgba(15,23,42,0.06)] backdrop-blur-xl">
        <div>
          <p className="text-[11px] uppercase tracking-[0.28em] text-slate-400">Seguridad</p>
          <h2 className="mt-2 text-2xl font-medium text-slate-950">Cambiar contraseña</h2>
        </div>

        <div className="grid gap-4 max-w-md">
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-2">Contraseña actual</label>
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              className="app-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-2">Nueva contraseña</label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="app-input"
            />
          </div>
        </div>

        <div className="flex items-center gap-4">
          <button
            type="submit"
            disabled={pwSaving || !currentPassword || !newPassword}
            className="rounded-[22px] bg-slate-900 hover:bg-slate-800 text-white px-6 py-4 font-medium transition disabled:opacity-50"
          >
            {pwSaving ? 'Cambiando...' : 'Cambiar contraseña'}
          </button>
          {pwMsg.text && (
            <span className={`text-sm font-medium ${pwMsg.type === 'error' ? 'text-red-600' : 'text-emerald-600'}`}>
              {pwMsg.text}
            </span>
          )}
        </div>
      </motion.form>
    </motion.div>
  );
}
